import {
  Box,
  Button,
  Container,
  ExpandableSection,
  Flashbar,
  Grid,
  Header,
  Input,
  Link,
  SpaceBetween,
  TokenGroup,
} from '@amzn/awsui-components-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventsAPI } from '@/src/api';
import { ChallengeUtils, Challenge, ChallengeReviewableSection } from '@/src/types/Challenge';
import { NullableNumber } from '@/src/types/common';
import { Event } from '@/src/types/Event';
import { EventLabSummary } from '@/src/types/EventLabSummary';
import { Lab } from '@/src/types/LabModels';
import { ResourceDeploymentItem } from '@/src/types/ResourceDeployment';
import { User } from '@/src/types/User';
import { getDaysHoursMinutes } from '@/src/utils/event-time.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { preProdLogger } from '@/src/utils/log.utils';
import { getHostURL } from '@/src/utils/url.utils';
import { ConfirmModal } from '../../../common/ConfirmModal';
import LabAccountTable from '../../../common/Labs/LabAccountTable';
import LabShutoffStatusBadge from '../../../common/LabShutoffStatusBadge';
import SecretKey from '../../../common/SecretKey';
import LabStatusCountdown from '../../../events/eventsCommon/labStatusCountdown/LabStatusCountdown';
import { ChallengeReviewPopover } from '../../challengesCommon/ChallengeReviewPopover';

interface ActiveTestEventProps {
  challenge: Challenge;
  testEventName: string;
  user: User | null;
  eventsApi: EventsAPI;
  onRedeployTestEvent: () => void;
  onUpdateTesters: (testers: string[]) => void;
  onTerminateTestEvent: () => void;
  onExtendTestEvent: (hours: number) => void;
}

const ActiveTestEvent: React.FC<ActiveTestEventProps> = ({
  challenge,
  testEventName,
  user,
  eventsApi,
  onRedeployTestEvent,
  onUpdateTesters,
  onTerminateTestEvent,
  onExtendTestEvent,
}) => {
  const { t } = useTranslation();

  const [activeTestItems, setActiveTestItems] = useState([
    {
      onDismiss: () => setActiveTestItems([]),
      content: <>{t(i18nKeys.challenges.challengeDetails.flashbar.noLabAccounts)}</>,
      id: 'no-active-lab-accounts',
    },
  ]);

  const [testEventLabSummary, setTestEventLabSummary] = useState<EventLabSummary | null>();
  const [testEvent, setTestEvent] = useState<Event | null>();
  const [testEventLabs, setTestEventLabs] = useState<Lab[]>();
  const [testerItems, setTesterItems] = useState<{ label: string; dismissLabel: string }[]>([]);
  const [testers, setTesters] = useState<string[]>([]);
  const [tester, setTester] = useState('');
  const [eventRemainingDuration, setEventRemainingDuration] = useState<NullableNumber>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const intervalId = useRef<number>();
  // modal inputs & states
  const [hours, setHours] = useState('');

  const [confirmTerminateVisibile, setConfirmTerminateVisible] = useState(false);
  const [confirmHoursVisible, setConfirmHoursVisible] = useState(false);

  useEffect(() => {
    loadTestEventLabSummary();

    // load the event
    eventsApi
      .getEvent(testEventName)
      .then((eventData) => {
        setTestEvent(eventData.event);
        // get the list of facilitators who will be testers
        setTesters(eventData.event.getFacilitators());
      })
      .catch((err) => preProdLogger(err));
  }, [testEventName]);

  useEffect(() => {
    if (testers) {
      const newTesterItems: { label: string; dismissLabel: string }[] = [];
      testers.forEach((testerEmail: string) => {
        newTesterItems.push({
          label: testerEmail,
          dismissLabel: `${t(i18nKeys.challenges.challengeDetails.labels.removeTag)}: ${testerEmail}`,
        });
      });
      setTesterItems(newTesterItems);
    }
  }, [testers]);

  useEffect(() => {
    setEventRemainingDuration(testEventLabSummary?.timeRemainingMillis || null);
    intervalId.current = setInterval(
      () => setEventRemainingDuration((eventDuration) => (eventDuration ? eventDuration - 1000 : null)),
      1000
    );
    return () => clearInterval(intervalId.current);
  }, [testEventLabSummary]);

  const loadTestEventLabSummary = () => {
    if (ChallengeUtils.isCollaborator(challenge, user)) {
      // load the event lab summary
      setRefreshing(true);
      eventsApi
        .getEventLabSummary(testEventName)
        .then(async (eventLabSummary) => {
          if (eventLabSummary) {
            // fetch the labs for the event and add them to the event lab summary
            eventLabSummary.labs = await eventsApi.getEventLabs(
              testEventName,
              Object.keys(eventLabSummary.labStatusSnapshots)
            );

            const eventLabs = eventLabSummary.getLabsAsList();

            for (const lab of eventLabs) {
              if (!Lab.isInFinalStatus(lab.status)) {
                const resourceDeploymentItems: ResourceDeploymentItem[] =
                  await eventsApi.getLabResourceDeploymentHistory(lab);
                lab.applyResourceTaskHistory(resourceDeploymentItems);
              }
            }
            setTestEventLabs(eventLabs);

            setTestEventLabSummary(eventLabSummary);
          } else {
            setTestEventLabSummary(null);
            setTestEventLabs([]);
          }
        })
        .catch((err) => preProdLogger(err))
        .finally(() => setRefreshing(false));
    }
  };

  const onTerminate = () => {
    // set other properties that should be emptied when `testEventName` is nullified for consistency
    setTestEvent(null);
    setTestEventLabSummary(null);
    setTestEventLabs([]);

    onTerminateTestEvent();
    setConfirmTerminateVisible(false);
  };

  const onExtend = () => {
    if (hours) {
      onExtendTestEvent(Number(hours));
    }
    setConfirmHoursVisible(false);
  };

  const addTester = () => {
    setTesters([...testers, tester]);
    setTester('');
  };

  const getTestEventChallengeVersion = () => {
    if (!testEventLabSummary) {
      return null;
    }
    if (!testEventLabSummary.challengeDescriptors) {
      return null;
    }
    if (testEventLabSummary.challengeDescriptors.length < 1) {
      return null;
    }
    return testEventLabSummary.challengeDescriptors[0].version;
  };

  return (
    <>
      <SpaceBetween size="s" direction="vertical">
        <Container
          header={
            <Header variant="h2" actions={<ChallengeReviewPopover section={ChallengeReviewableSection.TESTING} />}>
              {t(i18nKeys.challenges.challengeDetails.headings.testing)}
            </Header>
          }
        >
          {t(i18nKeys.challenges.challengeDetails.text.oneClickTestEventDescription)}
        </Container>
        <ExpandableSection
          variant="container"
          header={<Header variant="h2">{t(i18nKeys.events.eventDetails.headers.access)}</Header>}
          data-testid="access-expandable"
        >
          <Box margin={{ top: 'm' }}>
            <div className="container-table-header">
              <Grid gridDefinition={[{ colspan: 2 }, { colspan: 5 }, { colspan: 2 }, { colspan: 3 }]}>
                <div className="table-divider">
                  <strong>{t(i18nKeys.events.eventDetails.headers.eventType.header)}</strong>
                </div>
                <div>
                  <strong>{t(i18nKeys.events.eventDetails.headers.link)}</strong>
                </div>
                <div className="table-divider">
                  <strong>{t(i18nKeys.events.eventDetails.headers.secretKey)}</strong>
                </div>
                <div>
                  <strong>{t(i18nKeys.events.eventDetails.headers.status)}</strong>
                </div>
              </Grid>
            </div>
            <div style={{ padding: '10px 0px 10px 0px', marginBottom: '-20px' }}>
              <Grid gridDefinition={[{ colspan: 2 }, { colspan: 5 }, { colspan: 2 }, { colspan: 3 }]}>
                <React.Fragment>
                  <div>{testEventLabSummary && t(i18nKeys.events.eventDetails.labels.testEvent)}</div>
                  <div>
                    {testEventLabSummary?.eventName && (
                      <Link
                        external
                        href={`${getHostURL()}/${testEventLabSummary?.eventName}` || ''}
                        data-testid="octe-link"
                      >
                        {`${getHostURL()}/${testEventLabSummary?.eventName}`}
                      </Link>
                    )}
                  </div>
                  <div>
                    <SecretKey secretKey={testEventLabSummary?.eventCode} />
                  </div>
                  <div>
                    <LabStatusCountdown
                      isTest
                      includeSeconds={false}
                      inline
                      status={testEventLabSummary?.eventStatus}
                      labStartTime={testEventLabSummary?.labStartTime || ''}
                      labEndTime={testEventLabSummary?.labEndTime || ''}
                    />
                    <span style={{ marginRight: '20px' }} />
                    <LabShutoffStatusBadge
                      challengeDescriptors={testEventLabSummary?.challengeDescriptors}
                      shutoffStatus={testEventLabSummary?.shutoffStatus}
                      testEventShutoffStatus={testEventLabSummary?.testEventShutoffStatus}
                    />
                  </div>
                </React.Fragment>
              </Grid>
              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }]}>
                <div>
                  <strong>{`${t(i18nKeys.challenges.challengeDetails.text.timeRemaining)}: `}</strong>{' '}
                  {getDaysHoursMinutes(eventRemainingDuration)}
                </div>
                <div>
                  <Link
                    external
                    href={`/events/${testEventLabSummary?.eventName}` || ''}
                    data-testid="octe-details-link"
                  >
                    {t(i18nKeys.challenges.challengeDetails.text.testEventDetailPage)}
                  </Link>
                </div>
              </Grid>
            </div>
          </Box>
        </ExpandableSection>
        {testEvent && (
          <ExpandableSection
            variant="container"
            data-testid="testers-expandable"
            header={<Header variant="h2">{t(i18nKeys.challenges.challengeDetails.headings.testers)}</Header>}
          >
            <SpaceBetween size="s" direction="vertical">
              <p>{t(i18nKeys.challenges.challengeDetails.text.testersDescription)}</p>
              <SpaceBetween size={'m'} direction={'horizontal'}>
                <Input
                  onChange={({ detail }) => setTester(detail.value)}
                  value={tester}
                  placeholder={'user@example.com'}
                  data-testid="testers-email-input"
                />
                <Button iconName="add-plus" onClick={addTester} data-testid="testers-add-tester">
                  {t(i18nKeys.challenges.challengeDetails.buttons.addTester)}
                </Button>
              </SpaceBetween>
              <TokenGroup
                onDismiss={({ detail: { itemIndex } }) => {
                  setTesters([...testers.slice(0, itemIndex), ...testers.slice(itemIndex + 1)]);
                  setTesterItems([...testerItems.slice(0, itemIndex), ...testerItems.slice(itemIndex + 1)]);
                }}
                items={testerItems}
              />
              <div style={{ float: 'right' }}>
                <Button data-testid="save-testers-button" variant="primary" onClick={() => onUpdateTesters(testers)}>
                  {t(i18nKeys.challenges.challengeDetails.buttons.saveTesters)}
                </Button>
              </div>
            </SpaceBetween>
          </ExpandableSection>
        )}
        <ExpandableSection
          variant="container"
          data-testid="controls-expandable"
          header={<Header variant="h2">{t(i18nKeys.challenges.challengeDetails.headings.controls)}</Header>}
        >
          {getTestEventChallengeVersion() !== challenge.version && (
            <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
              {user?.isChallengeBuilder && (
                <Button data-testid="challenge-testing-upload" iconName="upload" onClick={onRedeployTestEvent}>
                  {t(i18nKeys.challenges.challengeDetails.buttons.pushLatestVersion)}
                </Button>
              )}
              <div>
                {t(i18nKeys.challenges.challengeDetails.text.updateTestChallengeVersion)}
                <br />
                {challenge.version && (
                  <strong>
                    {t(i18nKeys.challenges.challengeDetails.text.revisionBehind)}:{' '}
                    {challenge.version - (getTestEventChallengeVersion() || 0)}
                  </strong>
                )}
              </div>
            </Grid>
          )}
          <SpaceBetween size="s" direction="vertical">
            <p>{t(i18nKeys.challenges.challengeDetails.text.extendTestEventWarning)}</p>
            <div style={{ float: 'right' }}>
              <SpaceBetween size="m" direction="horizontal">
                <Button
                  data-testid="challenge-testing-extend"
                  iconName={'status-pending'}
                  onClick={() => setConfirmHoursVisible(true)}
                >
                  {t(i18nKeys.challenges.challengeDetails.buttons.extendTestEvent)}
                </Button>
                <Button
                  data-testid="challenge-testing-terminate"
                  iconName={'status-negative'}
                  onClick={() => setConfirmTerminateVisible(true)}
                >
                  {t(i18nKeys.challenges.challengeDetails.buttons.terminateTestEvent)}
                </Button>
              </SpaceBetween>
            </div>
          </SpaceBetween>
        </ExpandableSection>
        <ExpandableSection
          variant="container"
          data-testid="labs-expandable"
          header={<Header variant="h2"> {t(i18nKeys.challenges.challengeDetails.headings.labAccounts)}</Header>}
        >
          {challenge.awsAccountBased ? (
            <SpaceBetween size={'l'}>
              <Button
                iconName="refresh"
                onClick={() => void loadTestEventLabSummary()}
                data-testid="labs-refresh"
                loading={refreshing}
              >
                {t(i18nKeys.general.refresh)}
              </Button>
              <LabAccountTable
                labs={testEventLabs || []}
                filterable={false}
                showLabProviderStatus
                showChallenge={false}
                paginate={false}
                loading={refreshing}
              />
            </SpaceBetween>
          ) : (
            <Flashbar items={activeTestItems} />
          )}
        </ExpandableSection>
      </SpaceBetween>
      <ConfirmModal
        message={t(i18nKeys.general.areYouSure)}
        visible={confirmTerminateVisibile}
        confirmBtnLabel={t(i18nKeys.challenges.challengeDetails.yesLabels.continue)}
        onConfirm={onTerminate}
        onCancel={() => setConfirmTerminateVisible(false)}
        submitButtonId="terminate-submit-button"
        data-testid="terminate-test-event-modal"
      />
      <ConfirmModal
        title={t(i18nKeys.challenges.challengeDetails.titles.extendTestEvent)}
        message={t(i18nKeys.challenges.challengeDetails.messages.extendTestEvent)}
        visible={confirmHoursVisible}
        confirmBtnLabel={t(i18nKeys.general.submit)}
        textInput={hours}
        onTextInput={setHours}
        onConfirm={onExtend}
        onCancel={() => setConfirmHoursVisible(false)}
        submitButtonId="extend-submit-button"
        data-testid="extend-test-event-modal"
      />
    </>
  );
};

export default ActiveTestEvent;
